import moment from 'moment';

const partsFormatters = new Map();
const supportedZones = new Map();

function partsInZone(zone, instantMs) {
  if (!partsFormatters.has(zone)) {
    partsFormatters.set(zone, new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }));
  }
  const formatter = partsFormatters.get(zone);
  return formatter.formatToParts(new Date(instantMs)).reduce((parts, part) => ({ ...parts, [part.type]: part.value }), {});
}

function dateInZone(zone, instantMs) {
  const { year, month, day } = partsInZone(zone, instantMs);
  return `${year}-${month}-${day}`;
}

function zoneOffsetMinutes(zone, instantMs) {
  const { year, month, day, hour, minute, second } = partsInZone(zone, instantMs);
  const wallUtc = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
  return Math.round((wallUtc - Math.floor(instantMs / 1000) * 1000) / 60000);
}

function hasStableZoneOffset(zone, now = Date.now()) {
  const offset = zoneOffsetMinutes(zone, now);
  for (let day = 1; day <= 366; day += 1) {
    if (zoneOffsetMinutes(zone, now - day * 86400000) !== offset) return false;
  }
  return true;
}

function wallClockToInstant(date, time, zone) {
  const wall = moment.utc(`${date}T${time}`, 'YYYY-MM-DDTHH:mm:ss', true);
  if (!wall.isValid()) return null;
  const wallMs = wall.valueOf();
  try {
    const offsets = [zoneOffsetMinutes(zone, wallMs - 86400000), zoneOffsetMinutes(zone, wallMs + 86400000)];
    const candidates = offsets.map(offset => wallMs - offset * 60000).sort((a, b) => a - b);
    const exact = candidates.find(instant => {
      const parts = partsInZone(zone, instant);
      return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}` === `${date}T${time}`;
    });
    if (exact !== undefined) return moment.utc(exact);
    if (offsets[0] === offsets[1]) return null;
    let [before, after] = candidates;
    while (after - before > 1000) {
      const middle = Math.floor((before + after) / 2000) * 1000;
      if (zoneOffsetMinutes(zone, middle) === offsets[0]) before = middle;
      else after = middle;
    }
    return moment.utc(after);
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
}

function browserZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
}

function isSupportedZone(zone) {
  if (supportedZones.has(zone)) return supportedZones.get(zone);
  try {
    const supported = Boolean(new Intl.DateTimeFormat('en-US', { timeZone: zone }));
    supportedZones.set(zone, supported);
    return supported;
  } catch (error) {
    if (error instanceof RangeError) {
      supportedZones.set(zone, false);
      return false;
    }
    throw error;
  }
}

const zoneLabels = new Map();

function zoneLabel(zone) {
  const city = zone.split('/').pop().replace(/_/g, ' ');
  let offset;
  try {
    offset = zoneOffsetMinutes(zone, Date.now());
  } catch (error) {
    if (!(error instanceof RangeError)) throw error;
    const fallback = `${city} (${zone})`;
    return fallback;
  }
  const sign = offset < 0 ? '−' : '+';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  const gmt = offset === 0 ? 'GMT' : `GMT${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`;
  if (!zoneLabels.has(zone)) {
    let name;
    try {
      name = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longGeneric' })
        .formatToParts(new Date()).find(part => part.type === 'timeZoneName').value;
    } catch (error) { name = null; }
    zoneLabels.set(zone, name && name !== city ? `${name} – ` : '');
  }
  return `${zoneLabels.get(zone)}${city} (${gmt})`;
}

function formatZonePreview(start, end, zone) {
  const city = zone.split('/').pop().replace(/_/g, ' ');
  return `${moment(start, 'HH:mm').format('h:mm A')} – ${moment(end, 'HH:mm').format('h:mm A')}${start > end ? ' (next day)' : ''} in ${city}`;
}

function zonedSettingsFields(settings) {
  const date = dateInZone(settings.TimeZone, Date.now());
  const localStart = moment.utc(settings.workingHoursStart, ['HH:mm:ss', 'HH:mm']).format('HH:mm:ss');
  const localEnd = moment.utc(settings.workingHoursEnd, ['HH:mm:ss', 'HH:mm']).format('HH:mm:ss');
  const start = wallClockToInstant(date, localStart, settings.TimeZone);
  const end = wallClockToInstant(date, localEnd, settings.TimeZone);
  if (!start || !end) return null;
  return {
    TimeZone: settings.TimeZone,
    LocalStartingFreeTime: localStart,
    LocalEndingFreeTime: localEnd,
    StartingFreeTime: start.format('HH:mm:ss'),
    EndingFreeTime: end.format('HH:mm:ss'),
  };
}

export { wallClockToInstant, browserZone, dateInZone, zonedSettingsFields, zoneLabel, formatZonePreview, isSupportedZone, zoneOffsetMinutes, hasStableZoneOffset };
